// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import InfoCircle from "@hardwoods/XDL/Icons/InfoCircle";
import { mergeWithCss } from "@hardwoods/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    billTo: getBillToState(state, state.context.session.billToId),
});

interface Props {
    extendedStyles?: LastPurchasedOnStyles;
}

type OwnProps = Props & HasProduct & ReturnType<typeof mapStateToProps>;

export interface LastPurchasedOnStyles {
    container?: InjectableCss;
    icon?: IconPresentationProps;
}

export const lastPurchasedOnStyles: LastPurchasedOnStyles = {
    container: {
        css: css`
            display: inline-flex;
            align-items: center;
            font-size: 12px;
            letter-spacing: 0;
            line-height: 14px;
            color: ${getColor("common.ui14")};
        `,
    },
    icon: {
        src: InfoCircle,
        color: "primary",
        size: 12,
        css: css`
            margin-right: 5px;
        `,
    },
};

const LastPurchasedOn: React.FC<OwnProps> = ({ product, billTo, language, extendedStyles }) => {
    const [formatedDate, setFormatedDate] = useState<JSX.Element[]>();
    const [styles] = useState(() => mergeWithCss(lastPurchasedOnStyles, extendedStyles));
    useEffect(() => {
        if (product.properties["lastPurchasedOn"] && billTo.value) {
            const countryCode = billTo.value.country?.abbreviation;

            setFormatedDate(
                getLocalizedDateTime({
                    dateTime: new Date(product.properties["lastPurchasedOn"]),
                    language: countryCode === "CA" ? ({ cultureCode: "en-ca" } as any) : language,
                    options: { month: "2-digit", day: "2-digit", year: "2-digit" },
                })
                    .split("")
                    .map((item, index) => {
                        const key = `LastPurchasedOn_${index}`;
                        if (item === "/") {
                            return (
                                <Typography
                                    weight="normal"
                                    css={css`
                                        opacity: 0.33;
                                    `}
                                    key={key}
                                >
                                    &nbsp;{item}
                                </Typography>
                            );
                        }
                        if (item === "-") {
                            return (
                                <Typography
                                    weight="normal"
                                    css={css`
                                        opacity: 0.33;
                                    `}
                                    key={key}
                                >
                                    {item}
                                </Typography>
                            );
                        }
                        return <React.Fragment key={uniqueId()}>{item}</React.Fragment>;
                    }),
            );
        }
    }, [product.properties["lastPurchasedOn"], billTo.value]);

    if (!formatedDate) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="last_purchased_on">
            <IconMemo {...styles.icon} />
            {translate("Purchased")}&nbsp;
            <Typography weight="bold">{formatedDate}</Typography>
        </StyledWrapper>
    );
};

export default withProduct(connect(mapStateToProps)(LastPurchasedOn));
